import {
	useCallback, useEffect, useRef, useState,
} from 'react';
import {
	OpentokSessionValue,
	ProbeBroadcast,
	ProbeConnectedUsers,
	ProbeOpentokSession,
	ProbeResponse,
	ProbeRoomSession,
	RoomSessionValue,
} from '../../connection/health';
import { Entity } from '../../types/interfaces';
import { ConnectedUser } from '../../stores/types/connection';
import { Broadcast } from '../../stores/types/broadcasting';
import {
	DEGRADED_LATENCY_MS,
	Probe,
	ProbeStatus,
	PROBE_KEYS,
	RoomHealth,
	worstStatus,
} from '../../components/health-dashboard/types';

interface UseRoomHealthArgs {
	roomId: number | null;
	token: string | null;
	intervalMs: number; /** 0 pauses polling; the caller can still trigger `refresh`. */
}

interface UseRoomHealthResult {
	health: RoomHealth | null;
	isRefreshing: boolean;
	refresh: () => void;
}

/**
 * Turns a raw probe response into a status the dashboard can render.
 *
 * `extract` pulls the value of interest out of the body and returns null when
 * the service answered but has nothing to report, which is a normal state for a
 * room that is idle - not a failure.
 */
export function toProbe<T>(
	response: ProbeResponse<any>,
	extract: (body: any) => T | null,
	emptyMessage: string,
): Probe<T> {
	const { httpStatus, latencyMs } = response;
	const base = { httpStatus, latencyMs };

	if (httpStatus === 0) {
		return {
			...base,
			status: ProbeStatus.Error,
			message: response.error ?? 'no response',
			data: null,
		};
	}

	if (httpStatus === 401 || httpStatus === 403) {
		return {
			...base,
			status: ProbeStatus.Error,
			message: 'not authorized for this room',
			data: null,
		};
	}

	if (httpStatus >= 500) {
		return {
			...base,
			status: ProbeStatus.Error,
			message: `service error (${httpStatus})`,
			data: null,
		};
	}

	// 204 No Content and 404 Not Found both mean "nothing provisioned here yet".
	if (httpStatus === 204 || httpStatus === 404) {
		return {
			...base,
			status: ProbeStatus.Empty,
			message: emptyMessage,
			data: null,
		};
	}

	if (httpStatus >= 400) {
		return {
			...base,
			status: ProbeStatus.Error,
			message: `unexpected response (${httpStatus})`,
			data: null,
		};
	}

	const data = extract(response.body);
	if (data === null || data === undefined) {
		return {
			...base,
			status: ProbeStatus.Empty,
			message: emptyMessage,
			data: null,
		};
	}

	if (latencyMs > DEGRADED_LATENCY_MS) {
		return {
			...base,
			status: ProbeStatus.Degraded,
			message: `slow response (${latencyMs}ms)`,
			data,
		};
	}

	return { ...base, status: ProbeStatus.Ok, data };
}

/** Worst status across every probe, for the header summary. */
export function overallStatus(health: RoomHealth | null): ProbeStatus {
	if (!health) return ProbeStatus.Empty;

	return worstStatus(PROBE_KEYS.map((key) => health[key].status));
}

const entityValue = <T>(body: Entity | null): T | null => (body?.entityValue ?? null) as T | null;

/**
 * The broadcast entity nests one level deeper than the others: sync writes
 * `entityValue: { broadcast: {...} }`, which is why BroadcastReducer reads
 * `entityValue.broadcast` too. Reading `entityValue` here left every field
 * undefined and the panel blank.
 */
const broadcastValue = (body: Entity | null): Broadcast | null => (
	(body?.entityValue?.broadcast ?? null) as Broadcast | null
);

/**
 * Polls the server side view of one meeting's services. Every probe runs in
 * parallel each tick, and a slow or failing service never blocks the others.
 */
export default function useRoomHealth({
	roomId,
	token,
	intervalMs,
}: UseRoomHealthArgs): UseRoomHealthResult {
	const [health, setHealth] = useState<RoomHealth | null>(null);
	const [isRefreshing, setIsRefreshing] = useState(false);
	const mounted = useRef(true);
	const inFlight = useRef(false);

	useEffect(() => {
		mounted.current = true;
		return () => { mounted.current = false; };
	}, []);

	const runProbes = useCallback(async () => {
		if (!roomId || !token || inFlight.current) return;

		inFlight.current = true;
		setIsRefreshing(true);

		try {
			const [session, opentok, broadcast, users] = await Promise.all([
				ProbeRoomSession(token, roomId),
				ProbeOpentokSession(token, roomId),
				ProbeBroadcast(token, roomId),
				ProbeConnectedUsers(token, roomId),
			]);

			if (!mounted.current) return;

			setHealth({
				fetchedAt: Date.now(),
				roomSession: toProbe<RoomSessionValue>(
					session,
					entityValue,
					'no open room session',
				),
				opentok: toProbe<OpentokSessionValue>(
					opentok,
					entityValue,
					'no OpenTok session provisioned',
				),
				broadcast: toProbe<Broadcast>(
					broadcast,
					broadcastValue,
					'no broadcast for this room',
				),
				connectedUsers: toProbe<ConnectedUser[]>(
					users,
					(body) => (Array.isArray(body) && body.length ? body : null),
					'nobody connected',
				),
			});
		} finally {
			inFlight.current = false;
			if (mounted.current) setIsRefreshing(false);
		}
	}, [roomId, token]);

	useEffect(() => {
		runProbes();

		if (!intervalMs) return undefined;

		const timer = setInterval(runProbes, intervalMs);
		return () => clearInterval(timer);
	}, [runProbes, intervalMs]);

	return { health, isRefreshing, refresh: runProbes };
}
